import {useCallback} from 'react';

import type {
    CreateBillingPlanMutation,
    CreateBillingPlanMutationVariables,
} from 'web-app/api/clients/rest-types/create-billing-plan/create-billing-plan';
import {useRestMutation} from 'web-app/react/hooks/use-rest-mutation';
import * as Warnable from 'web-app/util/warnable';
import * as SentryService from 'web-app/services/sentry';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {CreateBillingPlan} from '../queries/create-billing-plan.rest-mutation';
import type * as Types from '../types';
import {useCreateAdhocPurchases} from './use-create-adhoc-purchases';
import {useCreateProratedItems} from './use-create-prorated-items';
import type {SubmissionData} from './use-submission-data';

export const useCreateCustomPeriod = () => {
    const {createProratedItems, isLoading: isProratedItemsCreationLoading} = useCreateProratedItems();
    const {createAdhocPurchasesForChildren, isLoading: isAdhocPurchasesCreationLoading} = useCreateAdhocPurchases();
    const [createBillingPlan, {loading: isBillingPlanLoading}] = useRestMutation<
        CreateBillingPlanMutation,
        CreateBillingPlanMutationVariables
    >(CreateBillingPlan);

    const createCustomPeriod = useCallback(
        async ({
            type,
            customPeriod,
            billingProfiles,
        }: {billingProfiles: Array<Types.BillingProfile>} & (
            | {type: 'start'; customPeriod: NonNullable<SubmissionData['customStartPeriod']>}
            | {type: 'end'; customPeriod: NonNullable<SubmissionData['customEndPeriod']>}
        )) => {
            const proratedProducts =
                type === 'start'
                    ? customPeriod?.customStartPeriodProratedProducts
                    : customPeriod?.customEndPeriodProratedProducts;
            const proratedSessions =
                type === 'start'
                    ? customPeriod?.customStartPeriodProratedSessions
                    : customPeriod?.customEndPeriodProratedSessions;

            const proratedTuitionsAndFees = await createProratedItems({
                proratedProducts,
                proratedSessions,
                billingProfiles,
            });

            if (!proratedTuitionsAndFees) {
                return Promise.resolve();
            }

            const proratedTuitions = Warnable.dispatchWarnings(
                Warnable.all(proratedTuitionsAndFees.proratedTuitions),
                warnings => {
                    SentryService.captureMessage(
                        `Prorated tuition for custom periods warnings: ${warnings.map(w => w.warning).join(', ')}`,
                    );
                },
            );

            const customPeriodPlans = customPeriod
                .plans({
                    selectedTuitions: proratedTuitions.filter(hasValue),
                })
                .map(plan => createBillingPlan({variables: plan}));

            const customPeriodAdhocPurchases = createAdhocPurchasesForChildren({
                adhocPurchases: customPeriod.adhocPurchases(proratedTuitionsAndFees.proratedFees),
            });

            return Promise.all([...customPeriodPlans, customPeriodAdhocPurchases]);
        },
        [createAdhocPurchasesForChildren, createBillingPlan, createProratedItems],
    );
    return {
        createCustomPeriod,
        isLoading: isBillingPlanLoading || isAdhocPurchasesCreationLoading || isProratedItemsCreationLoading,
    };
};
